import React, { useMemo, useState } from "react";
import { Alert, Box, Stack, TextField, MenuItem, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import { ticketService } from "../../api/services/ticketService";
import { slaService } from "../../api/services/slaService";
import { identityService } from "../../api/services/identityService";
import { projectService } from "../../api/services/projectService";
import { useAuth } from "../../contexts/AuthContext";
import { PageHeader } from "../../components/Common";
import KanbanBoard from "../../components/KanbanBoard";
import AssigneeAvatarFilter from "../../components/AssigneeAvatarFilter";

export default function TicketBoard() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { me } = useAuth();

  const [teamId, setTeamId] = useState("");
  const [statusId, setStatusId] = useState("");
  const [priorityId, setPriorityId] = useState("");
  const [scope, setScope] = useState("all"); // "all" | "mine"
  const [pickedAssigneeId, setPickedAssigneeId] = useState(null); // a specific person, picked via avatar

  const { data: project } = useQuery({ queryKey: ["project", projectId], queryFn: () => projectService.get(projectId) });
  const { data: statuses } = useQuery({ queryKey: ["statuses"], queryFn: slaService.listStatuses, staleTime: Infinity });
  const { data: priorities } = useQuery({ queryKey: ["priorities"], queryFn: slaService.listPriorities, staleTime: Infinity });
  const { data: transitions } = useQuery({ queryKey: ["workflow-transitions"], queryFn: slaService.listTransitions, staleTime: Infinity });
  const { data: teams } = useQuery({ queryKey: ["teams"], queryFn: identityService.listTeams });
  const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: identityService.listEmployees });

  // Avatar strip shows the selected team's members once a team is picked
  // (matching the same narrowing used in ticket creation's assignee field),
  // otherwise everyone in the company.
  const selectedTeam = teams?.find((t) => t.id === teamId);
  const avatarCandidates = selectedTeam
    ? (employees || []).filter((e) => selectedTeam.member_employee_ids.includes(e.id))
    : (employees || []);

  // "Mine" with no resolvable employee_id (e.g. an admin account whose ATS
  // role is attached via platform_user_id, not a real Employee row) can't be
  // filtered server-side -- gate the query rather than silently sending an
  // unfiltered request that would look identical to "All Tickets".
  const canFilterMine = Boolean(me?.employee_id);
  const effectiveAssigneeId = pickedAssigneeId || (scope === "mine" ? me?.employee_id : undefined);
  const boardParams = {
    project_id: projectId,
    team_id: teamId || undefined,
    status_id: statusId || undefined,
    priority_id: priorityId || undefined,
    assignee_employee_id: effectiveAssigneeId || undefined,
  };

  const { data: tickets, isLoading } = useQuery({
    queryKey: ["tickets-board", boardParams],
    queryFn: () => ticketService.listBoard(boardParams),
    enabled: Boolean(projectId) && (scope !== "mine" || pickedAssigneeId || canFilterMine),
  });

  const handleScopeChange = (value) => {
    setScope(value);
    setPickedAssigneeId(null);
  };

  const handleAvatarPick = (employeeId) => {
    setPickedAssigneeId(employeeId);
    setScope("all"); // an explicit person takes over from the All/Mine toggle
  };

  const priorityById = useMemo(() => Object.fromEntries((priorities || []).map((p) => [p.id, p])), [priorities]);
  const employeeById = useMemo(() => Object.fromEntries((employees || []).map((e) => [e.id, e])), [employees]);

  const sortedStatuses = useMemo(
    () => [...(statuses || [])].sort((a, b) => a.sort_order - b.sort_order),
    [statuses]
  );

  const cardsByColumn = useMemo(() => {
    const map = {};
    for (const s of sortedStatuses) map[s.id] = [];
    for (const t of tickets || []) {
      if (!map[t.status_id]) map[t.status_id] = [];
      map[t.status_id].push(t);
    }
    return map;
  }, [tickets, sortedStatuses]);

  const allowedPairs = useMemo(() => {
    const set = new Set();
    for (const t of transitions || []) set.add(`${t.from_status_id}-${t.to_status_id}`);
    return set;
  }, [transitions]);

  const isDropAllowed = (fromId, toId) => {
    if (!transitions || transitions.length === 0) return true; // company hasn't configured a workflow yet
    return allowedPairs.has(`${fromId}-${toId}`);
  };

  const moveMutation = useMutation({
    mutationFn: ({ ticketId, statusId }) => ticketService.update(ticketId, { status_id: statusId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tickets-board"] }),
    onError: (err) => {
      enqueueSnackbar(err.response?.data?.detail || "Couldn't move that ticket", { variant: "error" });
      queryClient.invalidateQueries({ queryKey: ["tickets-board"] });
    },
  });

  const renderCard = (ticket) => {
    const priority = priorityById[ticket.priority_id];
    const assignee = employeeById[ticket.assignee_employee_id];
    return (
      <Box onClick={() => navigate(`/tickets/${ticket.id}`)}>
        <Typography variant="caption" color="text.secondary">{ticket.ticket_number}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>{ticket.subject}</Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {priority ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: priority.color }} />
              <Typography variant="caption">{priority.name}</Typography>
            </Stack>
          ) : <span />}
          <Typography variant="caption" color="text.secondary">
            {assignee ? assignee.name : "Unassigned"}
          </Typography>
        </Stack>
      </Box>
    );
  };

  return (
    <Box>
      <PageHeader title={project ? `${project.name} · Board` : "Board"} subtitle="Drag a card to change its status" />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, v) => v && handleScopeChange(v)}>
          <ToggleButton value="all">All Tickets</ToggleButton>
          <ToggleButton value="mine">My Tickets</ToggleButton>
        </ToggleButtonGroup>
        <AssigneeAvatarFilter employees={avatarCandidates} selectedId={pickedAssigneeId} onChange={handleAvatarPick} />
        <TextField
          select size="small" label="Status" value={statusId} sx={{ width: 160 }}
          onChange={(e) => setStatusId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {(statuses || []).map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Priority" value={priorityId} sx={{ width: 150 }}
          onChange={(e) => setPriorityId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {(priorities || []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Team" value={teamId} sx={{ width: 170 }}
          onChange={(e) => { setTeamId(e.target.value); setPickedAssigneeId(null); }}
        >
          <MenuItem value="">All</MenuItem>
          {(teams || []).map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
        </TextField>
      </Stack>

      {scope === "mine" && !canFilterMine ? (
        <Alert severity="info">Your account isn't linked to an employee record, so "My Tickets" can't be filtered.</Alert>
      ) : (
        <KanbanBoard
          columns={sortedStatuses}
          cardsByColumn={cardsByColumn}
          loading={isLoading}
          renderCard={renderCard}
          isDropAllowed={isDropAllowed}
          onCardDrop={(ticketId, _fromStatusId, toStatusId) => moveMutation.mutate({ ticketId, statusId: toStatusId })}
        />
      )}
    </Box>
  );
}
